import { useEffect, useMemo, useState, useRef } from "react";
import { Leaf, Plus, Search, Upload } from "lucide-react";
import {
  paginateProjects,
  type Project,
  type Repository,
  type ProjectPage,
  type ProjectListItem,
  type ProjectPageQuery,
} from "@proof/core";
import { Select, Button, Modal } from "./components";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { ProjectThumbnail, CatalogProjectThumbnail } from "./ProjectThumbnail";
type ProjectRow = Project | ProjectListItem;
import {
  canDeleteProject,
  deleteProjects,
  type ProjectDeletionResult,
} from "./project-deletion";

/** Workspace landing page: every project, with the ways to start a new one. */
export function ProjectsHome({
  projects,
  repository,
  onPage,
  refreshToken,
  loadError,
  onOpen,
  onNew,
  onImport,
  onArchive,
  onDelete,
}: {
  projects: Project[];
  repository?: Repository;
  onPage?: (rows: Project[]) => void;
  refreshToken?: number;
  loadError?: string;
  onOpen: (id: string) => void;
  onNew: () => void;
  onImport: () => void;
  onArchive: (project: Project, archived: boolean) => Promise<void>;
  onDelete: (project: Project) => Promise<void>;
}) {
  const transport = useWorkspaceTransport();
  const [search, setSearch] = useState("");
  const [view, setView] =
    useState<NonNullable<ProjectPageQuery["status"]>>("active");
  const [deleting, setDeleting] = useState<Project[]>();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState("");
  const [promoting, setPromoting] = useState<ProjectRow[]>();
  const [graphicsScope, setGraphicsScope] = useState<"company" | "site">(
    "company",
  );
  const [graphicsReport, setGraphicsReport] = useState("");
  const [failureReport, setFailureReport] = useState<Project>();
  const loadProject = async (project: ProjectRow) => {
    if ("pages" in project) return project;
    const full = await repository?.get(project.tenantId, project.id);
    if (!full) throw Error("Project is no longer available. Refresh the list.");
    return full;
  };
  const openProject = (project: ProjectRow) => {
    if (!project.id.startsWith("project_import_failed_"))
      return onOpen(project.id);
    void loadProject(project)
      .then(setFailureReport)
      .catch((error) => setError(String(error.message || error)));
  };
  const [deletionResult, setDeletionResult] = useState<ProjectDeletionResult>();
  const remote = !transport.demo && !!repository?.page;
  const [page, setPage] = useState(0),
    [limit, setLimit] = useState(25);
  const [query, setQuery] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!remote) return;
    const timer = setInterval(() => setRevision((value) => value + 1), 10000);
    return () => clearInterval(timer);
  }, [remote]);
  const [remotePage, setRemotePage] = useState<ProjectPage<ProjectRow>>();
  const [pageLoading, setPageLoading] = useState(remote);
  const [pageError, setPageError] = useState("");
  const [working, setBusy] = useState(false);
  const busy = working;
  const onPageRef = useRef(onPage);
  onPageRef.current = onPage;
  const canCreate = transport.can("tenant.documents.manage"),
    canImport = canCreate && transport.can("tenant.assets.manage");
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search);
      setPage(0);
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    if (!remote || !repository?.page) return;
    const controller = new AbortController();
    let active = true;
    if (!remotePage) setPageLoading(true);
    setPageError("");
    const fetchPage = repository.catalog
      ? repository.catalog.bind(repository)
      : repository.page.bind(repository);
    void fetchPage(
      transport.principal!.tenantId,
      { offset: page * limit, limit, search: query, status: view },
      controller.signal,
    )
      .then((result) => {
        if (active) {
          setRemotePage(result);
          const actualPage = Math.floor(result.offset / limit);
          if (actualPage !== page) setPage(actualPage);
          if (!repository.catalog)
            onPageRef.current?.(result.items as Project[]);
        }
      })
      .catch((cause) => {
        if (active && !controller.signal.aborted)
          setPageError(
            cause instanceof Error
              ? cause.message
              : "Projects could not be loaded.",
          );
      })
      .finally(() => {
        if (active) setPageLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [
    remote,
    repository,
    transport.principal?.tenantId,
    page,
    limit,
    query,
    view,
    revision,
    refreshToken,
  ]);
  const localPage = useMemo(
    () =>
      remote
        ? undefined
        : paginateProjects(projects, {
            offset: page * limit,
            limit,
            search: query,
            status: view,
          }),
    [remote, projects, page, limit, query, view],
  );
  const result = remote ? remotePage : localPage;
  const shown = result?.items || [];
  const total = result?.total || 0,
    offset = result?.offset || 0;
  const currentPage = Math.floor(offset / limit);
  const selection = shown.filter((p) => selected.has(p.id));
  const allSelected = shown.length > 0 && selection.length === shown.length;
  const run = async (work: () => Promise<void>, clearSelection = true) => {
    setBusy(true);
    setError("");
    setDeletionResult(undefined);
    try {
      await work();
      if (clearSelection) setSelected(new Set());
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The project action could not be completed.",
      );
    } finally {
      setBusy(false);
      if (remote) setRevision((value) => value + 1);
    }
  };
  const archive = (rows: ProjectRow[], archived: boolean) =>
    run(async () => {
      for (const project of rows) {
        if ((project.status === "archived") !== archived)
          await onArchive(await loadProject(project), archived);
      }
    });
  return (
    <div className="overview projects-home">
      <div className="page-intro">
        <div>
          <span className="eyebrow">YOUR WORK</span>
          <h1>
            Projects<span className="heading-dot">.</span>
          </h1>
          <p>Pick up a project where you left it, or start something new.</p>
        </div>
        <div className="button-row">
          <Button disabled={busy || !canImport} onClick={onImport}>
            <Upload size={16} />
            Import document
          </Button>
          <Button kind="primary" disabled={busy || !canCreate} onClick={onNew}>
            <Plus size={16} />
            New project
          </Button>
        </div>
      </div>
      {deletionResult && (
        <section aria-label="Deletion summary">
          <p role="status">
            Deleted {deletionResult.deleted}{" "}
            {deletionResult.deleted === 1 ? "project" : "projects"}. Skipped{" "}
            {deletionResult.skipped.length}.
          </p>
          {deletionResult.skipped.length > 0 && (
            <ul>
              {deletionResult.skipped.map(({ project, reason }) => (
                <li key={project.id}>
                  <strong>{project.name}</strong>: {reason}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {loadError ? (
        <section className="panel padded">
          <h2>Workspace unavailable</h2>
          <p role="alert">{loadError}</p>
        </section>
      ) : !remote && !projects.length ? (
        <section className="panel padded projects-empty">
          <h2>No projects yet</h2>
          <p className="muted">
            Create a project from scratch or import existing artwork to get
            started.
          </p>
          <div className="button-row">
            <Button
              kind="primary"
              disabled={busy || !canCreate}
              onClick={onNew}
            >
              <Plus size={16} />
              New project
            </Button>
            <Button disabled={busy || !canImport} onClick={onImport}>
              <Upload size={16} />
              Import document
            </Button>
          </div>
        </section>
      ) : (
        <>
          <div className="documents-heading">
            <div>
              <h2>
                Projects <span className="count">{total}</span>
              </h2>
              <p className="muted">
                Select projects or use the actions on each row.
              </p>
            </div>
            <div className="projects-filters">
              <label className="projects-status-filter">
                Status
                <Select
                  aria-label="Project status"
                  disabled={working}
                  value={view}
                  onChange={(event) => {
                    setView(event.target.value as typeof view);
                    setPage(0);
                    setSelected(new Set());
                  }}
                >
                  <option value="active">Active projects</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="failed">Import failed</option>
                  <option value="archived">Archived projects</option>
                  <option value="all">All projects</option>
                </Select>
              </label>
              <div className="search-input">
                <Search size={15} />
                <input
                  aria-label="Search projects"
                  placeholder="Search projects…"
                  disabled={working}
                  maxLength={200}
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setSelected(new Set());
                  }}
                />
              </div>
            </div>
          </div>
          {pageLoading && <p role="status">Loading projects…</p>}
          {pageError && (
            <div role="alert">
              <p>{pageError}</p>
              <Button
                disabled={working || pageLoading}
                onClick={() => setRevision((value) => value + 1)}
              >
                Retry loading projects
              </Button>
            </div>
          )}
          {error && !deleting && (
            <p role="alert" className="error-text">
              {error}
            </p>
          )}
          {canCreate && (
            <div className="projects-selection-bar">
              <span>
                <span role="status">{selection.length} selected</span>{" "}
                <small>· Current page only</small>
              </span>
              <div className="button-row">
                {transport.can("tenant.assets.manage") && (
                  <Button
                    disabled={busy || !selection.length}
                    onClick={() => setPromoting(selection)}
                  >
                    Import graphics to site / company
                  </Button>
                )}
                <Button
                  disabled={
                    busy || !selection.some((p) => p.status !== "archived")
                  }
                  onClick={() => void archive(selection, true)}
                >
                  Archive selected
                </Button>
                <Button
                  disabled={
                    busy || !selection.some((p) => p.status === "archived")
                  }
                  onClick={() => void archive(selection, false)}
                >
                  Restore selected
                </Button>
                <Button
                  disabled={busy || !selection.length}
                  onClick={() =>
                    // Opening the confirmation keeps the selection for Cancel.
                    void run(
                      async () =>
                        setDeleting(
                          await Promise.all(selection.map(loadProject)),
                        ),
                      false,
                    )
                  }
                >
                  Delete selected
                </Button>
              </div>
            </div>
          )}
          <div className="projects-grid-scroll" aria-busy={pageLoading}>
            <table className="projects-grid" aria-label="Projects">
              <thead>
                <tr>
                  {canCreate && (
                    <th className="project-selection">
                      <input
                        type="checkbox"
                        aria-label="Select all shown projects"
                        disabled={busy || !shown.length}
                        checked={allSelected}
                        ref={(input) => {
                          if (input)
                            input.indeterminate =
                              selection.length > 0 && !allSelected;
                        }}
                        onChange={(event) =>
                          setSelected(
                            event.target.checked
                              ? new Set(shown.map((p) => p.id))
                              : new Set(),
                          )
                        }
                      />
                    </th>
                  )}
                  <th>Project</th>
                  <th>Status</th>
                  <th className="project-details">Details</th>
                  <th className="project-actions-heading">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr
                    className="project-tile"
                    key={p.id}
                    data-selected={selected.has(p.id) || undefined}
                  >
                    {canCreate && (
                      <td className="project-selection">
                        <input
                          type="checkbox"
                          aria-label={`Select ${p.name}`}
                          disabled={busy}
                          checked={selected.has(p.id)}
                          onChange={(event) =>
                            setSelected((current) => {
                              const next = new Set(current);
                              if (event.target.checked) next.add(p.id);
                              else next.delete(p.id);
                              return next;
                            })
                          }
                        />
                      </td>
                    )}
                    <td>
                      <div className="project-identity">
                        {"pages" in p ? (
                          <ProjectThumbnail project={p} />
                        ) : (
                          <CatalogProjectThumbnail
                            project={p}
                            load={() => loadProject(p)}
                          />
                        )}
                        <div>
                          <button
                            className="document-card project-name"
                            aria-label={`Open ${p.name}`}
                            disabled={busy}
                            onClick={() => openProject(p)}
                          >
                            {p.name}
                          </button>
                          {p.description && (
                            <p className="project-description">
                              {p.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge ${p.status === "published" ? "green" : ""}`}
                      >
                        {p.id.startsWith("project_import_failed_")
                          ? "Import failed"
                          : p.status === "archived"
                            ? "Archived"
                            : p.status === "published"
                              ? "Published"
                              : "Draft"}
                      </span>
                    </td>
                    <td className="project-details">
                      {"fields" in p ? p.fields.length : p.fieldCount} fields ·{" "}
                      {"outputs" in p ? p.outputs.length : p.outputCount} output
                      profiles
                    </td>
                    <td>
                      <div className="button-row project-actions">
                        <Button disabled={busy} onClick={() => openProject(p)}>
                          {p.id.startsWith("project_import_failed_")
                            ? "View report"
                            : "Open"}
                        </Button>
                        {canCreate && (
                          <>
                            <Button
                              disabled={busy}
                              onClick={() =>
                                void archive([p], p.status !== "archived")
                              }
                            >
                              {p.status === "archived" ? "Restore" : "Archive"}
                            </Button>
                            {canDeleteProject(p) && (
                              <Button
                                disabled={busy}
                                onClick={() =>
                                  void run(async () =>
                                    setDeleting([await loadProject(p)]),
                                  )
                                }
                              >
                                Delete
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <nav className="projects-pagination" aria-label="Project pages">
            <span>
              Showing {total ? offset + 1 : 0}–{offset + shown.length} of{" "}
              {total} projects
            </span>
            <label>
              Per page{" "}
              <Select
                aria-label="Projects per page"
                disabled={busy}
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(0);
                  setSelected(new Set());
                }}
              >
                {[25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
            </label>
            <Button
              disabled={busy || offset === 0}
              onClick={() => {
                setPage(Math.max(0, page - 1));
                setSelected(new Set());
              }}
            >
              Previous page
            </Button>
            <span>
              Page {currentPage + 1} of {Math.max(1, Math.ceil(total / limit))}
            </span>
            <Button
              disabled={busy || offset + shown.length >= total}
              onClick={() => {
                setPage((value) => value + 1);
                setSelected(new Set());
              }}
            >
              Next page
            </Button>
          </nav>
          {!shown.length && !pageLoading && (
            <p className="muted projects-no-match">
              {search.trim()
                ? `No projects match “${search}”.`
                : `No ${view} projects.`}
            </p>
          )}
        </>
      )}
      {graphicsReport && <p role="status">{graphicsReport}</p>}
      {promoting && (
        <Modal
          title="Import project graphics"
          onClose={() => {
            if (!busy) setPromoting(undefined);
          }}
        >
          <p>
            Copy graphics from {promoting.length} selected project
            {promoting.length === 1 ? "" : "s"} into a shared library. The
            originals remain attached to their projects. Fonts are already
            shared across the site.
          </p>
          <label>
            Destination
            <Select
              aria-label="Graphics library destination"
              value={graphicsScope}
              disabled={busy}
              onChange={(event) =>
                setGraphicsScope(event.target.value as typeof graphicsScope)
              }
            >
              <option value="company">Company library</option>
              <option value="site">Site library</option>
            </Select>
          </label>
          <div className="button-row">
            <Button disabled={busy} onClick={() => setPromoting(undefined)}>
              Cancel
            </Button>
            <Button
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const results: string[] = [];
                  for (const project of promoting) {
                    try {
                      const result = await transport.client.request<{
                        imported: number;
                      }>(
                        `/projects/${encodeURIComponent(project.id)}/graphics/promote`,
                        {
                          method: "POST",
                          body: JSON.stringify({ scope: graphicsScope }),
                        },
                      );
                      results.push(
                        `${project.name}: ${result.imported} graphics imported.`,
                      );
                    } catch (error) {
                      results.push(
                        `${project.name}: ${error instanceof Error ? error.message : "Import failed"}`,
                      );
                    }
                  }
                  setGraphicsReport(results.join(" "));
                  setPromoting(undefined);
                })
              }
            >
              {busy ? "Importing graphics…" : "Import graphics"}
            </Button>
          </div>
        </Modal>
      )}
      {deleting && (
        <Modal
          title={
            deleting.length === 1
              ? "Delete unused project?"
              : "Delete unused projects?"
          }
          onClose={() => {
            if (!busy) setDeleting(undefined);
          }}
        >
          <p>
            Delete{" "}
            {deleting.length === 1
              ? `“${deleting[0].name}”`
              : `${deleting.length} selected projects`}{" "}
            from this workspace? Projects that have been published, proofed, or
            used for preview or rendering will be skipped. If a deletion fails,
            the remaining projects will still be processed. A summary will list
            skipped projects and their reasons when finished. Archive protected
            projects instead.
          </p>
          {deleting.length > 1 && (
            <ul>
              {deleting.map((project) => (
                <li key={project.id}>{project.name}</li>
              ))}
            </ul>
          )}
          {error && (
            <p role="alert" className="error-text">
              {error}
            </p>
          )}
          <div className="button-row">
            <Button disabled={busy} onClick={() => setDeleting(undefined)}>
              Cancel
            </Button>
            <Button
              kind="primary"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const result = await deleteProjects(deleting, onDelete);
                  setDeletionResult(result);
                  setDeleting(undefined);
                })
              }
            >
              {deleting.length === 1 ? "Delete project" : "Delete projects"}
            </Button>
          </div>
        </Modal>
      )}
      {failureReport && (
        <Modal
          title={`Import report: ${failureReport.name}`}
          onClose={() => setFailureReport(undefined)}
        >
          <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {failureReport.instructions}
          </pre>
          <Button onClick={() => setFailureReport(undefined)}>Close</Button>
        </Modal>
      )}
      <div className="workspace-footnote">
        <Leaf size={15} />
        <span>
          {transport.demo
            ? "Your demo workspace is saved locally. Design freely, without connecting a database."
            : "Projects and published versions are stored by your workspace API."}
        </span>
      </div>
    </div>
  );
}
